import { useState, useEffect } from 'react';
import { differenceInCalendarDays, format } from 'date-fns';
import { calendarApi, bookingsApi, usersApi, ApiRequestError, FRIENDS_USER_ID, FRIENDS_USER_NAME } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import type { PublicUser } from '@apartment-booking/shared';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRange: { start: Date; end: Date } | null;
  onBookingComplete: () => void;
}

interface AvailabilityInfo {
  available: boolean;
  hasConflict: boolean;
  hasPendingConflict: boolean;
  hasOwnPending: boolean;
  pendingCount: number;
  earliestAvailable: string | null;
  latestAvailable: string | null;
}

export default function BookingModal({
  isOpen,
  onClose,
  selectedRange,
  onBookingComplete,
}: BookingModalProps) {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';

  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availability, setAvailability] = useState<AvailabilityInfo | null>(null);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

  // Owner-only: book for another user
  const [users, setUsers] = useState<PublicUser[]>([]);
  const [forUserId, setForUserId] = useState<string>('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');

  // Check availability when dates change
  useEffect(() => {
    if (!isOpen || !selectedRange) {
      setAvailability(null);
      return;
    }

    const checkAvailability = async () => {
      setIsCheckingAvailability(true);
      try {
        const startStr = format(selectedRange.start, 'yyyy-MM-dd');
        const endStr = format(selectedRange.end, 'yyyy-MM-dd');
        const result = await calendarApi.checkAvailability(startStr, endStr);
        setAvailability(result);
      } catch (err) {
        console.error('Failed to check availability:', err);
      } finally {
        setIsCheckingAvailability(false);
      }
    };

    checkAvailability();
  }, [isOpen, selectedRange]);

  // Fetch users for owner dropdown
  useEffect(() => {
    if (!isOpen || !isOwner) return;

    const fetchUsers = async () => {
      try {
        const { users } = await usersApi.list();
        setUsers(users.filter(u => u.is_active));
      } catch (err) {
        console.error('Failed to fetch users:', err);
      }
    };

    fetchUsers();
  }, [isOpen, isOwner]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setMessage('');
      setError(null);
      setForUserId('');
      setGuestName('');
      setGuestEmail('');
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedRange) return;

    // Validate guest name for Friends bookings
    if (forUserId === FRIENDS_USER_ID && !guestName.trim()) {
      setError('Guest name is required for Friends bookings');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const startStr = format(selectedRange.start, 'yyyy-MM-dd');
      const endStr = format(selectedRange.end, 'yyyy-MM-dd');

      await bookingsApi.create({
        start_date: startStr,
        end_date: endStr,
        message: message.trim() || undefined,
        for_user_id: forUserId || undefined,
        guest_name: forUserId === FRIENDS_USER_ID ? guestName.trim() : undefined,
        guest_email: forUserId === FRIENDS_USER_ID && guestEmail.trim() ? guestEmail.trim() : undefined,
      });

      onBookingComplete();
      onClose();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to submit booking request');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const formatDateRange = () => {
    if (!selectedRange) return '';
    const start = format(selectedRange.start, 'MMM d, yyyy');
    const end = format(selectedRange.end, 'MMM d, yyyy');
    if (start === end) return start;
    return `${start} - ${end}`;
  };

  const getNights = () => {
    if (!selectedRange) return 0;
    // End date is the checkout date (noon). Nights are whole calendar days between check-in and checkout.
    return Math.max(0, differenceInCalendarDays(selectedRange.end, selectedRange.start));
  };

  const canSubmit =
    getNights() > 0 && availability && !availability.hasConflict && !availability.hasOwnPending;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Request Booking</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Date display */}
            <div className="mb-4">
              <label className="label">Selected Dates</label>
              <div className="text-lg font-medium text-gray-900">
                {formatDateRange()}
              </div>
              <div className="text-sm text-gray-500">
                {getNights()} night{getNights() !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Owner-only: Book for another user */}
            {isOwner && (
              <div className="mb-4 space-y-3">
                <div>
                  <label htmlFor="forUser" className="label">
                    Book for
                  </label>
                  <select
                    id="forUser"
                    value={forUserId}
                    onChange={(e) => {
                      setForUserId(e.target.value);
                      if (e.target.value !== FRIENDS_USER_ID) {
                        setGuestName('');
                      }
                    }}
                    className="input"
                  >
                    <option value="">Myself</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                        {u.id === FRIENDS_USER_ID ? ' (for non-members)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {forUserId === FRIENDS_USER_ID && (
                  <>
                    <div>
                      <label htmlFor="guestName" className="label">
                        Guest Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="guestName"
                        type="text"
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        className="input"
                        placeholder="Enter the guest's name"
                        maxLength={100}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        This name will appear on the calendar as "{FRIENDS_USER_NAME}: {guestName || '...'}"
                      </p>
                    </div>
                    <div>
                      <label htmlFor="guestEmail" className="label">
                        Guest Email
                      </label>
                      <input
                        id="guestEmail"
                        type="email"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        className="input"
                        placeholder="guest@example.com"
                        maxLength={255}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        If provided, the guest will receive arrival and departure reminder emails
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Availability status */}
            {isCheckingAvailability ? (
              <div className="mb-4 p-3 bg-gray-50 rounded-md flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                <span className="text-sm text-gray-600">Checking availability...</span>
              </div>
            ) : availability ? (
              <div className="mb-4">
                {availability.hasConflict ? (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-center gap-2 text-red-700">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-medium">Dates unavailable</span>
                    </div>
                    <p className="mt-1 text-sm text-red-600">
                      These dates overlap with an existing booking.
                    </p>
                    {(availability.earliestAvailable || availability.latestAvailable) && (
                      <div className="mt-2 text-sm text-red-600">
                        <p className="font-medium">Suggestion:</p>
                        {availability.earliestAvailable && (
                          <p>• Arrival available from {format(new Date(availability.earliestAvailable + 'T12:00:00'), 'MMM d')} at noon</p>
                        )}
                        {availability.latestAvailable && (
                          <p>• Must depart by {format(new Date(availability.latestAvailable + 'T12:00:00'), 'MMM d')} at noon</p>
                        )}
                      </div>
                    )}
                  </div>
                ) : availability.hasOwnPending ? (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <div className="flex items-center gap-2 text-yellow-700">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-medium">Pending request exists</span>
                    </div>
                    <p className="mt-1 text-sm text-yellow-600">
                      You already have a pending request for these dates.
                    </p>
                  </div>
                ) : availability.hasPendingConflict ? (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <div className="flex items-center gap-2 text-yellow-700">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-medium">
                        {availability.pendingCount} pending request{availability.pendingCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-yellow-600">
                      Other requests are pending for overlapping dates. You can still submit your request.
                    </p>
                  </div>
                ) : (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                    <div className="flex items-center gap-2 text-green-700">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="font-medium">Dates available</span>
                    </div>
                    <p className="mt-1 text-xs text-green-600">
                      Arrival: noon on first day · Departure: noon on last day
                    </p>
                  </div>
                )}
              </div>
            ) : null}

            {/* Message field */}
            <div className="mb-4">
              <label htmlFor="message" className="label">
                Message (optional)
              </label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="input min-h-[80px]"
                placeholder="Add a note about your stay..."
                maxLength={500}
              />
              <div className="text-xs text-gray-500 mt-1">
                {message.length}/500 characters
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !canSubmit}
                className="btn-primary flex-1"
              >
                {isSubmitting ? 'Submitting...' : 'Request Booking'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
